/**
 * Result type for operations that may succeed or fail.
 * Similar to Rust's Result<T, E> or functional Either types.
 *
 * @template T - The type of the success value
 *
 * @example
 * ```ts
 * const result: TResult<number> = { ok: true, data: 42 };
 * const error: TResult<number> = { ok: false, error: new Error("Failed") };
 *
 * if (result.ok) {
 *   console.log(result.data); // TypeScript knows data exists
 * } else {
 *   console.error(result.error); // TypeScript knows error exists
 * }
 * ```
 */
export type TResult<T> = { ok: true; data: T } | { ok: false; error: unknown };

/**
 * Executes a synchronous function and wraps the result in a TResult type.
 * Catches any thrown errors and returns them in a structured format.
 *
 * @template T - The return type of the function
 * @param fn - The synchronous function to execute
 * @returns A TResult containing either the successful data or the caught error
 *
 * @example
 * ```ts
 * const result = tryCatch(() => JSON.parse('{"valid": "json"}'));
 * if (result.ok) {
 *   console.log(result.data); // { valid: "json" }
 * } else {
 *   console.error("Parse failed:", result.error);
 * }
 *
 * const failResult = tryCatch(() => JSON.parse('invalid json'));
 * // failResult.ok === false
 * ```
 */
export const tryCatch = <T>(fn: () => T): TResult<T> => {
  try {
    return { ok: true, data: fn() };
  } catch (error) {
    return { ok: false, error };
  }
};

/**
 * Executes an asynchronous function and wraps the result in a TResult type.
 * Catches any thrown errors or rejected promises and returns them in a structured format.
 *
 * @template T - The resolved type of the promise
 * @param fn - The async function to execute
 * @returns A Promise that resolves to a TResult containing either the successful data or the caught error
 *
 * @example
 * ```ts
 * const result = await tryCatchAsync(async () => {
 *   const response = await fetch('/api/data');
 *   return response.json();
 * });
 *
 * if (result.ok) {
 *   console.log("Data:", result.data);
 * } else {
 *   console.error("Request failed:", result.error);
 * }
 * ```
 */
export const tryCatchAsync = async <T>(fn: () => Promise<T>): Promise<TResult<T>> => {
  try {
    const data = await fn();
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error };
  }
};
